import React, { useMemo, useState, useEffect } from 'react';
import { Box, Modal } from '@material-ui/core';
import { Chess } from 'chess.js';
import {
  Chessboard,
  ChessboardDnDProvider,
  SparePiece,
} from 'react-chessboard';
import { filterGameData } from '../../util/ClipCall';
import { usePremium } from '../../util/PremiumFilter'; // Import the usePremium hook
import axios from 'axios';
import chessslogo from '../../images/chessslogo.png'; // Import chessslogo
import { firestore } from '../../firebase';
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";

// NEXT STEPS:
// 1. Make the game data fetch work with the new API
// 2. Make the video conversion work
// 3. Make the premium features work
// Ensure that new game modal popup after posting -> Click Next Game --> Modal doesnt appear ...

const NewGameAccessModal = ({ onClose, gameLink, gameId: propGameId }) => {
  const [modalOpen, setModalOpen] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showVideoTooltip, setShowVideoTooltip] = useState(false);
  const [gameData, setGameData] = useState(null);
  const [gameId, setGameId] = useState(propGameId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [processingVideo, setProcessingVideo] = useState(false);
  const { isPremiumUser } = usePremium(); // Get premium status from context

  const [tooltipText, setTooltipText] = useState('Copy to clipboard');
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  // Check if URL contains ctx=newGame and if we've already shown the modal this page load
  useEffect(() => {
    // If modal was already shown and closed during this page load, don't show it again
    if (sessionStorage.getItem(`gameModal-${gameId}`)) {
      setModalOpen(false);
      // Clean up URL if needed
      if (window.location.href.includes('?ctx=newGame')) {
        const cleanUrl = window.location.href.split('?')[0];
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }, [gameId]);

  // Extract game ID from the link or use the provided gameId prop
  useEffect(() => {
    // If gameId is directly provided as a prop, use it
    if (propGameId) {

      setGameId(propGameId);
      return;
    }

    // Otherwise extract from gameLink or current URL
    if (gameLink) {
      // Extract the game ID from the URL
      // The URL format is typically /games/GAME_ID or /games/GAME_ID?ctx=newGame
      const urlParts = gameLink.split('/');
      const gameIdWithParams = urlParts[urlParts.length - 1]; // Get the last part
      const extractedId = gameIdWithParams.split('?')[0]; // Remove any query parameters


      setGameId(extractedId);
    } else {
      // If no gameLink is provided, try to extract from current URL
      const currentUrl = window.location.href;
      if (currentUrl.includes('/games/')) {
        const urlParts = currentUrl.split('/games/');
        const gameIdWithParams = urlParts[1]; // Get the part after /games/
        const extractedId = gameIdWithParams.split('?')[0]; // Remove any query parameters


        setGameId(extractedId);
      }
    }
  }, [gameLink, propGameId]);

  // Clean up the URL without ctx=newGame parameter
  const getCleanUrl = () => {
    if (gameLink) {
      return gameLink.split('?')[0]; // Remove any query parameters
    }

    const currentUrl = window.location.href;
    return currentUrl.split('?')[0]; // Remove any query parameters
  };

  const handlePremiumClick = () => {
    if (isPremiumUser) {
      // For premium users, directly transform the game data
      if (isLoading || processingVideo) {
        alert("Please wait, operation in progress...");
        return;
      }

      if (!gameId) {
        alert("Game ID is not available. Please try again later.");
        return;
      }

      // Show processing state
      setProcessingVideo(true);

      try {
        // Get the current user from Firebase Auth
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          alert("You must be logged in to convert games.");
          setProcessingVideo(false);
          return;
        }

        // Fetch the game data directly from Firestore - same approach as AdminDashboard
        const gameRef = doc(firestore, 'games', gameId);
        getDoc(gameRef)
          .then((gameDoc) => {
            if (gameDoc.exists()) {
              // Game found, proceed with transformation
              const gameData = gameDoc.data();
              const filteredData = filterGameData(gameData);

              if (!filteredData) {
                throw new Error("Failed to process game data");
              }

              // Accessing specific data
              const arrowsAndSquares = filteredData.arrowsAndSquares || [];
              const originalMoves = filteredData.originalMoves || [];
              const pgn = filteredData.pgn || '';
              const videoTitle = filteredData.videoTitle || 'ChessThread.com';
              const gameFen = filteredData.fen || '';

              // Build the transformed data structure
              const transformedData = {
                takeScreenShot: false,
                videoUrl: '',
                isCapturingComplete: false,
                moves: originalMoves.map((move) => move.notation),
                frameRate: 1,
                startFrame: 1,
                endFrame: originalMoves.length,
                gameFen: gameFen,
                framesWithCustomPause: {},
                allFrameslength: {},
                ColoredMoves: [
                  {
                    move: 'New Game',
                    color: '',
                    Index: 0,
                    description: "",
                    time: 1,
                    descriptionColor: '#ffffff',
                    highlightedSquares: {},
                    arrows: [],
                  },
                  ...originalMoves.map((move, index) => {
                    const arrowsAndSquaresForMove = arrowsAndSquares[index] || {};
                    const highlightedSquares =
                      arrowsAndSquaresForMove.highlights &&
                        !Array.isArray(arrowsAndSquaresForMove.highlights)
                        ? arrowsAndSquaresForMove.highlights
                        : {};

                    const arrows = arrowsAndSquaresForMove.arrows || [];

                    // Transforming arrows to an object with explicit arrow names
                    const formattedArrows = arrows.reduce(
                      (acc, arrow, arrowIndex) => {
                        acc[`arrow${arrowIndex + 1}`] = {
                          from: arrow[0],
                          to: arrow[1],
                          color: arrow[2],
                        };
                        return acc;
                      },
                      {},
                    );

                    return {
                      move: move.notation,
                      color: index % 2 === 0 ? 'White' : 'Black',
                      time: 1,
                      Index: index + 1,
                      description: move.content || "",
                      descriptionColor: '#ffffff',
                      highlightedSquares: highlightedSquares,
                      arrows: formattedArrows,
                    };
                  }),
                ],
                copyOfMoves: [
                  'New Game',
                  ...originalMoves.map((move) => move.notation),
                ],
                isFlipped: false,
                gameName: '',
                signedInUser: {
                  name: currentUser.displayName || 'Anonymous',
                  role: 'user',
                  uid: currentUser.uid,
                  videoCount: 0,
                  subscriptionStatus: 'active', // Assuming premium users have active subscription
                  gamesCount: 1,
                },
                pgn: pgn || '',
                videoTitle: videoTitle,
                fontType: 'Original',
                fontColor: 'green',
                aspectRatio: '9:16',
                Url: '',
              };

              // Save to the ChessClip collection
              const newCollectionName = 'chessSocialGames';
              const gameDocRef = doc(firestore, newCollectionName, gameId);

              return setDoc(gameDocRef, transformedData).then(() => {
                setSuccessMessage('Game data processed successfully!');

                // Open ChessClip in a new tab with FEN parameter
                window.open(`https://www.chessclip.io/auth/login?gameId=${gameId}&uid=${currentUser.uid}&fen=${encodeURIComponent(gameFen)}`, "_blank");

                // Close the modal after success
                setTimeout(() => {
                  handleClose();
                }, 1000);
              });
            } else {
              throw new Error(`Game with ID ${gameId} not found in Firestore.`);
            }
          })
          .catch((error) => {
            console.error('Error processing game data:', error);
            alert(`Failed to process game: ${error.message}`);
          })
          .finally(() => {
            setProcessingVideo(false);
          });
      } catch (error) {
        console.error("Error starting transformation process:", error);
        alert("Error starting transformation process: " + error.message);
        setProcessingVideo(false);
      }
    } else {
      // For non-premium users, open premium page in new window
      window.open('/premium', '_blank');
    }
  };

  // Handle video conversion button click
  const handleMakeVideoClick = () => {
    if (isLoading) {
      alert("Please wait, game data is still loading...");
      return;
    }

    if (gameData) {
      try {
        // Use the filterGameData function from ClipCall.js
        const filteredData = filterGameData(gameData);


        // Show tooltip to indicate processing
        setShowVideoTooltip(true);
        setTimeout(() => setShowVideoTooltip(false), 2000);
      } catch (error) {

        alert("Error processing game data: " + error.message);
      }
    } else if (gameId) {
      // If we have a game ID but no data, direct them to use the Premium button instead
      setIsLoading(true);
      alert("Please use the 'Use Video Editor' button to process this game.");
      setIsLoading(false);
    } else {
      alert("Game data is not available. Please try again later.");
    }
  };

  // Handle closing the modal and cleaning up the URL
  const handleClose = () => {
    setModalOpen(false);

    // Clean up the URL by removing the ctx=newGame parameter if present
    if (window.location.href.includes('?ctx=newGame')) {
      const cleanUrl = window.location.href.split('?')[0];
      window.history.replaceState({}, document.title, cleanUrl);
    }

    // Set a flag in sessionStorage to remember that we've shown the modal for this game
    if (gameId) {
      sessionStorage.setItem(`gameModal-${gameId}`, 'shown');
    }

    if (onClose) onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getCleanUrl());
    setShowCopiedMessage(true);
    setTimeout(() => setShowCopiedMessage(false), 2000);
  };

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '600px',
        backgroundColor: '#000000',
        border: '2px solid #FFD700',
        borderRadius: '15px',
        boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)',
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Top section - game posted info */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '30px 30px 20px',
          borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
          backgroundColor: 'rgba(20, 20, 20, 0.8)'
        }}>
          <div style={{
            color: 'gold',
            fontSize: '30px',
            fontWeight: 'bold',
            marginBottom: '5px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textAlign: 'center'
          }}>
            Game Posted!
          </div>

          <div style={{
            color: '#58b047',
            fontSize: '24px',
            marginBottom: '15px',
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            Your game is now live on our platform.
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            backgroundColor: 'rgba(0,0,0,0.3)',
            border: '2px solid #FFD700',
            borderRadius: '8px',
            padding: '0',
            position: 'relative'
          }}>
            <span style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0px 20px',
              color: '#FFD700',
              fontSize: '18px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>Share Link:</span>
            <input
              type="text"
              value={getCleanUrl()}
              readOnly
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '16px',
                outline: 'none',
                padding: '10px'
              }}
            />
            <button
              onClick={handleCopy}
              style={{
                position: 'relative',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '15px 20px',
                color: showCopiedMessage ? '#58b047' : '#FFD700',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'color 0.2s ease'
              }}
            >
              <i className={showCopiedMessage ? "fas fa-check" : "fas fa-copy"}></i>
              {showCopiedMessage && (
                <div style={{
                  position: 'absolute',
                  top: '-25px',
                  right: '0',
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: '#58b047',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}>
                  Copied!
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Bottom section - video creation/continue options */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '25px 30px 30px',
          backgroundColor: 'rgba(0, 0, 0, 0.9)'
        }}>
          {successMessage && (
            <div style={{
              color: '#58b047',
              backgroundColor: 'rgba(88, 176, 71, 0.1)',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '15px',
              textAlign: 'center',
              fontWeight: 'bold'
            }}>
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div style={{
              color: 'red',
              backgroundColor: 'rgba(255, 0, 0, 0.1)',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '15px',
              textAlign: 'center'
            }}>
              {errorMessage}
            </div>
          )}

          {/* <div style={{
            color: 'white',
            fontSize: '28px',
            fontWeight: 'bold',
            marginBottom: '20px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <div><i className="fa-brands fa-youtube"></i></div>
            <div><i className="fa-brands fa-tiktok"></i></div>
            <div style={{ fontStyle: 'italic', }}>{isPremiumUser ? 'Create Social Media Video!' : 'Turn Game Into A Video?'}</div>

            <div><i className="fa-brands fa-instagram"></i></div>
            <div><i className="fa-brands fa-x-twitter"></i></div>

          </div> */}

          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {/* <button
              onClick={handlePremiumClick}
              disabled={processingVideo}
              style={{
                width: '100%',
                padding: '20px',
                backgroundColor: processingVideo ? '#666' : '#ff0050',
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                fontSize: '30px',
                fontWeight: 'bold',
                cursor: processingVideo ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                opacity: processingVideo ? 0.7 : 1
              }}
            >
              {processingVideo ? 'PROCESSING...' : (isPremiumUser ? 'Use Video Editor' : 'Upgrade To Premium')}
            </button> */}

            <button
              onClick={handleClose}
              style={{
                width: '100%',
                padding: '20px',
                backgroundColor: '#FFD700',
                border: 'none',
                borderRadius: '5px',
                color: 'black',
                fontSize: '30px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              Continue To My Game
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default NewGameAccessModal;
